import React, { PropsWithChildren, useContext } from 'react';
import { apiClient, PoiCategoryDto } from '../services/api';
import { fallbackPoiCategories } from './fallback-poi-categories';

export interface PoiCategory {
  readonly label: string;
  readonly value: string;
}

export interface AppConfig {
  readonly poiCategories: readonly PoiCategory[];
}

const AppConfigContext = React.createContext<AppConfig | undefined>(undefined);

export function useAppConfig() {
  const appConfig = useContext(AppConfigContext);
  if (appConfig === undefined) {
    throw new Error('useAppConfig must be used inside AppConfigProvider tree');
  }

  return appConfig;
}

export interface AppConfigProviderProps {
  appConfig: AppConfig;
}

export function AppConfigProvider({
  appConfig,
  children,
}: PropsWithChildren<AppConfigProviderProps>) {
  return (
    <AppConfigContext.Provider value={appConfig}>
      {children}
    </AppConfigContext.Provider>
  );
}

const APP_CONFIG_TIMEOUT = 2000;

export async function loadAppConfig() {
  const fallbackPoiCategoriesPromise = new Promise<PoiCategoryDto[]>(
    (resolve) => {
      setTimeout(() => resolve(fallbackPoiCategories), APP_CONFIG_TIMEOUT);
    },
  );

  const poiCategories = await Promise.race<PoiCategoryDto[]>([
    apiClient.getPoiCategories().catch(() => fallbackPoiCategories),
    fallbackPoiCategoriesPromise,
  ]);
  return {
    poiCategories: poiCategories.map((category) => ({
      label: category.name,
      value: category.canonicalId,
    })),
  };
}
